<template>
  <div id="users">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="users-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <TwoPeople :color="selectTheme.icon" class="users-icon" />
              {{ $t('users.title') }}
            </h1>
          </div>
          <v-card v-if="users" color="transparent" flat>
            <div
              v-if="canUseGlobalUserSearch"
              class="global-users-search-row px-0 pb-4 w-100"
            >
              <v-text-field
                v-model="globalUserSearch"
                :append-inner-icon="globalUserSearch ? '' : 'mdi-earth'"
                :label="$t('users.globalSearchLabel')"
                :hint="$t('users.globalSearchHint')"
                persistent-hint
                single-line
                hide-details="auto"
                clearable
                variant="solo"
                flat
                density="compact"
                color="#A5D867"
                class="global-users-search"
                :loading="globalSearchLoading"
                @update:model-value="onGlobalSearchInput"
              ></v-text-field>
            </div>
            <v-card-title
              class="px-0 pb-6 pt-0 d-flex align-center flex-wrap flex-sm-nowrap"
            >
              <span class="users-action-bar font-weight-bold"
                >{{ $t('users.totalUsers') }}: {{ visibleLeaders.length }}</span
              >
              <v-spacer></v-spacer>
              <v-select
                v-model="selectedMonth"
                :items="translatedMonths"
                :label="$t('users.filterByMonth')"
                density="compact"
                variant="solo"
                clearable
                flat
                hide-details
                color="#A5D867"
                class="mr-4 fixed-size-select"
                @update:model-value="filterByMonth(selectedMonth)"
              ></v-select>
              <v-text-field
                v-model="search"
                :append-inner-icon="search ? '' : 'mdi-magnify'"
                :label="$t('users.searchPlaceholder')"
                single-line
                hide-details
                clearable
                variant="solo"
                flat
                density="compact"
                color="#E8E9EC"
                class="fixed-size-search"
              ></v-text-field>
            </v-card-title>
          </v-card>
          <v-flex class="text-caption text-uppercase d-flex mb-3">
            <v-btn
              size="small"
              variant="text"
              min-width="inherit"
              height="inherit"
              disabled
              tile
              class="pl-0 pr-1 filter-btn"
              >{{ $t('users.filter') }}</v-btn
            >
            <v-btn
              size="small"
              variant="text"
              min-width="inherit"
              height="inherit"
              tile
              class="font-weight-bold pa-0 sort-btn"
              @click="userFilters('show-leaders')"
              >{{
                areLeadersVisible
                  ? $t('users.showAll')
                  : $t('users.showOnlyLeaders')
              }}</v-btn
            >
            <v-divider class="mx-4" vertical></v-divider>
            <v-btn
              size="small"
              variant="text"
              min-width="inherit"
              height="inherit"
              tile
              class="font-weight-bold pa-0 sort-btn"
              @click="userFilters('show-archived')"
              >{{
                areArchivedUsersVisible ? $t('users.hide') : $t('users.show')
              }}
              {{ $t('users.archived') }}</v-btn
            >
            <div class="d-flex px-2 ml-auto">
              <v-menu location="top left" :close-on-content-click="false">
                <template #activator="{ props }">
                  <v-icon v-bind="props" size="24">mdi-view-column</v-icon>
                </template>
                <v-list class="header-menu-list">
                  <v-list-item v-for="(h, i) in adminActions" :key="i">
                    <v-checkbox
                      v-model="selectedHeaders"
                      :value="h.key"
                      :label="h.title"
                      density="compact"
                      color="#A5D867"
                      hide-details
                      multiple
                      @update:model-value="setShowHeaders"
                    >
                    </v-checkbox>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-flex>
          <!--color="#592892"-->
          <v-snackbar
            v-model="snackbar"
            :timeout="timeout"
            location="bottom"
            class=""
          >
            <span v-html="snackbarMsg"></span>

            <template #actions="{ attrs }">
              <v-btn
                color="pink"
                variant="text"
                v-bind="attrs"
                @click="snackbar = false"
                class="font-weight-bold"
              >
                {{ $t('users.close') }}
              </v-btn>
            </template>
          </v-snackbar>
          <v-data-table
            :headers="showHeaders"
            fixed-header
            :loading="isloading || globalSearchLoading"
            :loading-text="$t('users.loadingUsers')"
            :no-data-text="$t('users.noUsersForChurch')"
            :items="visibleLeaders"
            :items-per-page="20"
            :header-props="{ sortIcon: '$sortarrowdown' }"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50, -1],
            }"
            v-model:expanded="expanded"
            expand-icon="$edit"
            item-key="email"
            :row-props="({ item }) => ({ class: row_classes(item) })"
            :single-expand="singleExpand"
            :search="search"
            class="elevation-1 users-table"
          >
            <template #progress>
              <v-progress-linear
                color="#592892"
                indeterminate
              ></v-progress-linear>
            </template>
            <!-- Avatar / URL -->
            <template #item.url="{ item }">
              <v-avatar v-if="item.url" start size="27">
                <v-img :src="item.url"></v-img>
              </v-avatar>
              <v-avatar v-else color="#cfebae" start size="27">
                <span>{{ shortHand(item.user_name) }}</span>
              </v-avatar>
            </template>

            <!-- Name Field -->
            <template #item.user_name="{ item }">
              <div v-if="showedit === item.id" class="user-name-edit-field">
                <v-form v-model="isFormValid">
                  <v-text-field
                    v-model="item.user_name"
                    :rules="[rules.required, rules.length(2)]"
                    persistent-hint
                    :hint="$t('users.forms.editUserName')"
                    class="user-input"
                    color="#A0C548"
                    variant="underlined"
                    density="compact"
                    :loading="isediting"
                    @update:model-value="queueEditUser(item)"
                    @keydown.enter.prevent="editUser(item)"
                  />
                </v-form>
              </div>
              <div v-else>
                {{ item.user_name }}
              </div>
            </template>

            <!-- Email + Role Select -->
            <template #item.email="{ item }">
              <div
                v-if="item.email"
                class="users-email"
                :class="{ active: showedit === item.id }"
              >
                <div>{{ item.email }}</div>
                <div
                  v-if="showedit === item.id"
                  class="possible-leader-checkbox"
                >
                  <v-checkbox
                    v-model="item.possibleLeader"
                    :label="$t('users.forms.possibleLeader')"
                    :true-value="true"
                    :false-value="null"
                    density="compact"
                    color="success"
                    hide-details
                    class="text-caption"
                    @update:model-value="editUser(item)"
                  />
                </div>
                <div v-else>
                  <span v-if="checkForLeader(item)">
                    <v-icon>mdi-account</v-icon>
                  </span>
                  <span v-else-if="item.possibleLeader === true">
                    <v-icon>mdi-account-outline</v-icon>
                  </span>
                </div>
              </div>
            </template>

            <!-- Phone (superAdmin / helpDesk only) -->
            <template
              #item.phoneNumber="{ item }"
              v-if="isAuthenticated && checkAuthorization(['superAdmin'])"
            >
              <div>{{ displayPhoneNumber(item.phoneNumber) }}</div>
            </template>

            <template #item.cid="{ item }">
              <span class="text-caption font-weight-medium text-medium-emphasis">{{
                item.cid != null && item.cid !== '' ? item.cid : '—'
              }}</span>
            </template>

            <!-- Group -->
            <template #item.group="{ item }">
              <div v-if="item.uid" :class="item.uid">
                {{ lookUpGroup(item.uid) }}
              </div>
            </template>

            <!-- Access -->
            <template #item.access="{ item }">
              <div
                v-if="
                  showedit === item.id && checkAuthorization(['superAdmin'])
                "
              >
                <v-select
                  v-model="item.roles"
                  :items="translatedUsersRoles"
                  item-title="title"
                  item-value="value"
                  multiple
                  chips
                  variant="underlined"
                  :item-color="selectTheme.item"
                  @update:model-value="editUser(item)"
                  class="roles-select"
                >
                  <template #chip="data">
                    <v-chip
                      :color="selectTheme.chip"
                      size="small"
                      :class="selectTheme.chipClass"
                    >
                      {{ data.item.title }}
                    </v-chip>
                  </template>
                </v-select>
              </div>
              <div v-else>
                <span v-for="(role, index) in item.roles" :key="index">
                  <span v-if="index != 0">, </span>
                  <span>{{
                    translatedUsersRoles.find((r) => r.value === role)?.title ||
                    role
                  }}</span>
                </span>
              </div>
            </template>

            <!-- Active Switch -->
            <template
              #item.active="{ item }"
              v-if="
                isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
              "
            >
              <div>
                <v-btn
                  :color="!item.active ? '' : '#A5D867'"
                  class="ma-0 pt-0 _activate-switch"
                  icon
                  variant="plain"
                  v-model="item.active"
                  :disabled="
                    !item.active && !checkAuthorization(['superAdmin'])
                  "
                  @click="activateUser(item)"
                >
                  <v-icon>mdi-circle-slice-8</v-icon>
                </v-btn>
              </div>
            </template>

            <!-- Joined -->
            <template #item.joined="{ item }">
              <div v-if="item.joined">{{ formatDate(item.joined) }}</div>
            </template>

            <!-- Activated -->
            <template #item.activated="{ item }">
              <div v-if="item.activated">
                {{ expires(item) }}
              </div>
            </template>

            <!-- Actions -->
            <template
              #item.actions="{ item }"
              v-if="
                isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
              "
            >
              <div class="action-icons">
                <span>
                  <v-tooltip location="bottom">
                    <template #activator="{ props }">
                      <span v-bind="props">
                        <v-icon
                          @click.native="
                            showedit = showedit === item.id ? null : item.id
                          "
                          class="edit-icon mr-6"
                        >
                          edit
                        </v-icon>
                      </span>
                    </template>
                    <span>Edit {{ item.user_name }}</span>
                  </v-tooltip>
                </span>
                <span>
                  <v-tooltip
                    v-if="checkAuthorization(['superAdmin', 'admin'])"
                    location="bottom"
                  >
                    <template #activator="{ props }">
                      <span
                        v-if="
                          !item.roles.includes('superAdmin') &&
                          !item.roles.includes('helpDesk')
                        "
                      >
                        <span v-bind="props">
                          <v-icon
                            @click.native="archiveUser(item)"
                            :class="{ archived: item.archived }"
                            class="archive-icon"
                          >
                            logout
                          </v-icon>
                        </span>
                      </span>
                    </template>
                    <span v-if="!item.archived"
                      >Archive {{ item.user_name }}</span
                    >
                    <span v-else>Un-Archive {{ item.user_name }}</span>
                  </v-tooltip>
                </span>
              </div>
            </template>
          </v-data-table>
          <v-flex xs12 class="d-flex mb-7 mt-12">
            <div class="mr-6" v-if="!isHelpDesk()">
              <v-btn
                v-if="
                  isAuthenticated && checkAuthorization(['superAdmin', 'admin'])
                "
                color="rgb(56, 57, 53)"
                class="elevation-0 font-weight-bold"
                :disabled="
                  isAuthenticated &&
                  !checkAuthorization(['superAdmin', 'admin'])
                "
                @click="deactivateAllUsers"
                >{{ $t('users.buttons.deactivateAll') }}</v-btn
              >
            </div>
            <div
              class="mr-6"
              v-if="checkAuthorization(['superAdmin']) && !isHelpDesk()"
            >
              <v-btn
                v-if="isAuthenticated && checkAuthorization(['superAdmin'])"
                color="error"
                class="elevation-0 font-weight-bold"
                :disabled="
                  isAuthenticated && !checkAuthorization(['superAdmin'])
                "
                @click="destroyUser"
                >{{ $t('users.buttons.annihilation') }}</v-btn
              >
            </div>
            <div
              class="mr-2"
              v-if="checkAuthorization(['superAdmin']) && !isHelpDesk()"
            >
              <v-btn
                color="error"
                class="elevation-0 font-weight-bold"
                @click="purgeDialog = true"
              >
                Purge Archived Courses
              </v-btn>
            </div>
            <v-btn
              v-if="
                isAuthenticated &&
                checkAuthorization(['owner']) &&
                checkAuthorization(['developer'])
              "
              color="#f502eb"
              class="elevation-0 font-weight-bold ml-4"
              @click="addTestUser"
            >
              {{ $t('users.buttons.addTestUser') }}
            </v-btn>
          </v-flex>

          <v-dialog v-model="purgeDialog" max-width="560px">
            <v-card>
              <v-card-title class="text-h6 font-weight-bold">
                Purge Archived Courses
              </v-card-title>
              <v-card-text>
                <div class="text-caption mb-4">
                  Select a user to remove all archived courses and related
                  storage files.
                </div>
                <v-menu transition="scale-transition">
                  <template #activator="{ props }">
                    <div class="purge-user-input">
                      <v-text-field
                        v-bind="props"
                        v-model="purgeSearch"
                        density="compact"
                        variant="outlined"
                        clearable
                        hide-details
                        class="purge-user-input-field purge-user-input-control"
                        placeholder="Search by name or email"
                        style="width: 100%"
                      ></v-text-field>
                    </div>
                  </template>
                  <v-list max-height="280" class="purge-user-list">
                    <v-list-item
                      v-for="user in filteredPurgeUsers"
                      :key="user.id || user.uid"
                      @click="selectPurgeUser(user)"
                    >
                      <v-list-item-title>{{ user.label }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-card-text>
              <v-card-actions class="justify-end">
                <v-btn variant="text" @click="purgeDialog = false">
                  Cancel
                </v-btn>
                <v-btn
                  color="error"
                  class="font-weight-bold"
                  :disabled="!selectedPurgeUser"
                  @click="confirmPurgeSelection"
                >
                  Purge
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import firebase from 'firebase'
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { formatDate } from '@/components/utilities/formatters'
  import { mapGetters } from 'vuex'
  import { confirmAction } from '@/components/utilities/SwalService'
  import Swal from 'sweetalert2'
  import TwoPeople from '@/assets/icons/TwoPeople.vue'
  import { currentTheme } from '@/plugins/vuetify'
  import { shortHand } from '@/helpers/stringUtils'
  import { purgeArchivedUserCourses } from '@/components/utilities/courseCleanup'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'Users',
    data() {
      return {
        snackbar: false,
        snackbarMsg: '',
        timeout: 6000,
        feedback: null,
        users: [],
        groups: [],
        expanded: [],
        singleExpand: true,
        isloading: true,
        isFormValid: false,
        isediting: false,
        showedit: false,
        search: '',
        globalUserSearch: '',
        /** Mirrors newfeatures/global-user-search active (Live). */
        globalUserSearchFeatureLive: false,
        /** When false, Help Desk does not see all-churches search (when feature is Live). */
        globalSearchHelpDeskAllowed: true,
        globalUserSearchGateUnsubs: null,
        globalSearchLoading: false,
        globalSearchDebounceHandle: null,
        editUserDebounceHandle: null,
        globalSearchRequestSeq: 0,
        activeUser: false,
        selectedPurgeUser: null,
        purgeSearch: '',
        purgeDialog: false,
        selectedHeaders: [],
        areArchivedUsersVisible: false,
        rules: {
          length: (len) => (v) =>
            (v || '').length >= len || `min ${len} characters required `,
          required: (v) => !!v || 'This field is required',
        },
        usersRoles: [],
        possibleLeaderOptions: [],
        dateFormatConfig: {
          dayOfWeekNames: [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ],
          dayOfWeekNamesShort: ['Su', 'Mo', 'Tu', 'We', 'Tr', 'Fr', 'Sa'],
          monthNames: [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
          ],
          monthNamesShort: [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ],
          // Timezone offset, in minutes (0 - UTC, 180 - Russia, undefined - current)
          timezone: 0,
        },
        selectedMonth: null,
        months: [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ],
        filtered: '',
        areLeadersVisible: false,
      }
    },
    components: {
      HowToUseThisPage,
      TwoPeople,
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      ...mapGetters('AppData', ['cid']),
      churchid() {
        return this.cid
      },
      canUseGlobalUserSearch() {
        const roles = this.$store.getters['UserData/getRoles'] || []
        if (!Array.isArray(roles)) return false
        if (!this.globalUserSearchFeatureLive) return false
        if (roles.includes('superAdmin')) return true
        if (roles.includes('helpDesk') && this.globalSearchHelpDeskAllowed) {
          return true
        }
        return false
      },
      selectTheme() {
        const isDark = currentTheme.value === 'dark'
        return {
          icon: isDark ? '#cecece' : '#383935',
          chip: isDark ? '#cfebae' : '#e0f0c2',
          item: isDark ? undefined : '#592892',
          chipClass: isDark ? 'chip-dark' : 'chip-light',
        }
      },
      /** Global search runs at 2+ chars; show CID to disambiguate church. */
      isGlobalUserSearchResultsView() {
        if (!this.canUseGlobalUserSearch) return false
        return (this.globalUserSearch || '').trim().length >= 2
      },
      showHeaders() {
        const base = this.adminActions.filter((s) =>
          this.selectedHeaders.includes(s.key)
        )
        if (!this.isGlobalUserSearchResultsView) {
          return base
        }
        const cidHeader = {
          title: this.$t('users.tableHeaders.cid'),
          key: 'cid',
          sortable: true,
          filterable: false,
          width: '120px',
        }
        const emailIdx = base.findIndex((h) => h.key === 'email')
        const out = base.slice()
        if (emailIdx >= 0) {
          out.splice(emailIdx + 1, 0, cidHeader)
        } else {
          out.unshift(cidHeader)
        }
        return out
      },
      adminActions() {
        const accessSortRaw = (a, b) => {
          const ra = ((a.roles || []).slice().sort().join(',') || '')
            .toString()
            .toLowerCase()
          const rb = ((b.roles || []).slice().sort().join(',') || '')
            .toString()
            .toLowerCase()
          return ra.localeCompare(rb)
        }

        const groupSortRaw = (a, b) => {
          const ga = this.groupNameByUidMap.get(a.uid) || ''
          const gb = this.groupNameByUidMap.get(b.uid) || ''
          return ga.localeCompare(gb, undefined, { sensitivity: 'base' })
        }

        let h = [
          {
            title: this.$t('users.tableHeaders.avatar'),
            sortable: false,
            filterable: false,
            key: 'url',
            default: true,
          },
          {
            title: this.$t('users.tableHeaders.name'),
            key: 'user_name',
            sortable: true,
            default: true,
          },
          {
            title: this.$t('users.tableHeaders.email'),
            sortable: true,
            key: 'email',
            default: true,
          },
          {
            title: this.$t('users.tableHeaders.group'),
            sortable: true,
            sortRaw: groupSortRaw,
            filterable: false,
            key: 'group',
            default: true,
          },
          {
            title: this.$t('users.tableHeaders.access'),
            sortable: true,
            sortRaw: accessSortRaw,
            filterable: false,
            key: 'access',
            default: true,
          },
          {
            title: this.$t('users.tableHeaders.active'),
            sortable: true,
            filterable: false,
            key: 'active',
            default: true,
            width: '105px',
          },
          {
            title: this.$t('users.tableHeaders.joined'),
            sortable: true,
            filterable: false,
            key: 'joined',
            default: false,
            width: '130px',
          },
          {
            title: this.$t('users.tableHeaders.license'),
            sortable: true,
            filterable: false,
            key: 'activated',
            default: true,
            width: '130px',
          },
          {
            title: this.$t('users.tableHeaders.actions'),
            sortable: false,
            filterable: false,
            key: 'actions',
            default: true,
          },
        ]
        if (this.isAuthenticated) {
          if (this.checkAuthorization(['admin']))
            h = [
              {
                title: this.$t('users.tableHeaders.avatar'),
                sortable: false,
                filterable: false,
                key: 'url',
                default: true,
              },
              {
                title: this.$t('users.tableHeaders.name'),
                key: 'user_name',
                sortable: true,
                default: true,
              },
              {
                title: this.$t('users.tableHeaders.email'),
                sortable: true,
                key: 'email',
                default: true,
              },
              {
                title: this.$t('users.tableHeaders.group'),
                sortable: true,
                sortRaw: groupSortRaw,
                filterable: false,
                key: 'group',
                default: true,
              },
              {
                title: this.$t('users.tableHeaders.access'),
                sortable: true,
                sortRaw: accessSortRaw,
                filterable: false,
                key: 'access',
                default: true,
              },
              {
                title: this.$t('users.tableHeaders.active'),
                sortable: true,
                filterable: false,
                key: 'active',
                default: true,
                width: '130px',
              },
              {
                title: this.$t('users.tableHeaders.license'),
                sortable: true,
                filterable: false,
                key: 'activated',
                default: true,
                width: '130px',
              },
              {
                title: this.$t('users.tableHeaders.actions'),
                sortable: false,
                filterable: false,
                key: 'actions',
                default: true,
              },
            ]
        }
        if (this.isAuthenticated && this.checkAuthorization(['superAdmin'])) {
          const phoneCol = {
            title: this.$t('users.tableHeaders.phone'),
            sortable: false,
            filterable: false,
            key: 'phoneNumber',
            default: true,
            width: '140px',
          }
          const emailIdx = h.findIndex((col) => col.key === 'email')
          if (emailIdx >= 0) {
            h = [
              ...h.slice(0, emailIdx + 1),
              phoneCol,
              ...h.slice(emailIdx + 1),
            ]
          } else {
            h.push(phoneCol)
          }
        }
        return h
      },
      translatedUsersRoles() {
        return [
          {
            text: this.$t('users.roles.admin'),
            value: 'admin',
            title: this.$t('users.roles.admin'),
          },
          {
            text: this.$t('users.roles.member'),
            value: 'member',
            title: this.$t('users.roles.member'),
          },
          {
            text: this.$t('users.roles.tester'),
            value: 'tester',
            title: this.$t('users.roles.tester'),
          },
          {
            text: this.$t('users.roles.dummy'),
            value: 'dummy',
            title: this.$t('users.roles.dummy'),
          },
        ]
      },
      translatedPossibleLeaderOptions() {
        return [
          {
            text: this.$t('users.status.yes'),
            value: true,
          },
          {
            text: this.$t('users.status.no'),
            value: false,
          },
        ]
      },
      translatedMonths() {
        return [
          this.$t('users.months.january'),
          this.$t('users.months.february'),
          this.$t('users.months.march'),
          this.$t('users.months.april'),
          this.$t('users.months.may'),
          this.$t('users.months.june'),
          this.$t('users.months.july'),
          this.$t('users.months.august'),
          this.$t('users.months.september'),
          this.$t('users.months.october'),
          this.$t('users.months.november'),
          this.$t('users.months.december'),
        ]
      },
      filteredUsers() {
        if (!this.filtered) return this.users
        return this.users.filter((item) => {
          const formatted = formatDate(item.joined)
          return formatted.slice(0, 3) === this.filtered.slice(0, 3)
        })
      },
      /** Matches lookUpGroup / Firestore group order: first leader match, else first member match. */
      groupNameByUidMap() {
        const map = new Map()
        const groups = this.groups || []
        for (const g of groups) {
          if (g.leader && !map.has(g.leader)) {
            map.set(g.leader, g.name || '')
          }
        }
        for (const g of groups) {
          const name = g.name || ''
          const members = Array.isArray(g.members) ? g.members : []
          for (const uid of members) {
            if (!map.has(uid)) {
              map.set(uid, name)
            }
          }
        }
        return map
      },
      purgeUserOptions() {
        return (this.users || []).map((user) => ({
          ...user,
          label: `${user.user_name || 'Unknown'} (${user.email || user.id})`,
        }))
      },
      filteredPurgeUsers() {
        const term = (this.purgeSearch || '').trim().toLowerCase()
        if (!term) return this.purgeUserOptions
        return this.purgeUserOptions.filter((user) =>
          user.label.toLowerCase().includes(term)
        )
      },
      visibleLeaders() {
        let allUsers
        if (this.areArchivedUsersVisible) {
          allUsers = this.filteredUsers.filter((c) => c.archived)
        } else if (this.areLeadersVisible) {
          allUsers = this.filteredUsers.filter(function (u) {
            return u.roles.includes('leader')
          })
        } else {
          allUsers = this.filteredUsers.filter((c) => !c.archived)
        }
        return allUsers
      },
    },
    watch: {
      churchid: {
        immediate: true,
        handler(newCid) {
          if (this.globalSearchDebounceHandle) {
            clearTimeout(this.globalSearchDebounceHandle)
            this.globalSearchDebounceHandle = null
          }
          this.globalUserSearch = ''
          if (newCid) {
            console.log('[WATCH] churchid is now available:', newCid)
            this.init(newCid)
          } else {
            console.warn('[WATCH] churchid is still undefined')
          }
        },
      },
    },
    methods: {
      formatDate,
      shortHand,
      displayPhoneNumber(phoneNumber) {
        const value =
          phoneNumber !== null && phoneNumber !== undefined
            ? String(phoneNumber).trim()
            : ''
        return value || '—'
      },
      async activateUser(user) {
        const actionKey = user.active ? 'deactivate' : 'activate'
        const expectedPlaceholder = this.$t(
          `users.dialogs.${actionKey}.inputPlaceholder`
        )
        const expectedWord = actionKey.toUpperCase()

        const confirmed = await confirmAction({
          title: `${this.$t(`users.dialogs.${actionKey}.title`)} <br>"${
            user.email
          }"`,
          html: this.$t(`users.dialogs.${actionKey}.html`),
          input: 'text',
          inputPlaceholder: this.$t(
            `users.dialogs.${actionKey}.inputPlaceholder`
          ),
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (
                matchesConfirmWord(value, expectedWord, expectedPlaceholder)
              ) {
                resolve()
              } else {
                resolve(this.$t(`users.dialogs.${actionKey}.inputValidator`))
              }
            })
          },
          confirmButtonText: this.$t(
            `users.dialogs.${actionKey}.confirmButton`
          ),
          cancelButtonText: this.$t(`users.dialogs.${actionKey}.cancelButton`),
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        try {
          const newStatus = !user.active
          await db
            .collection('users')
            .doc(user.id)
            .update({
              active: newStatus,
              activated: Date.now(),
              roles: firebase.firestore.FieldValue.arrayUnion(...user.roles),
            })
          user.active = newStatus // Local state update if needed
        } catch (err) {
          console.error('Failed to update user status:', err)
        }
      },
      queueEditUser(user) {
        if (this.editUserDebounceHandle) {
          clearTimeout(this.editUserDebounceHandle)
        }
        this.editUserDebounceHandle = setTimeout(() => {
          this.editUser(user)
        }, 800)
      },
      async editUser(user) {
        if (!user?.id) return

        this.isediting = true
        const userName = (user.user_name || '').trim() || null
        const possibleLeader =
          user.possibleLeader !== undefined ? user.possibleLeader : null

        try {
          await db.collection('users').doc(user.id).update({
            user_name: userName,
            roles: user.roles,
            possibleLeader: possibleLeader,
          })
          user.user_name = userName
        } catch (err) {
          console.error('Failed to update user:', err)
          this.snackbarMsg = 'Failed to save user name. Please try again.'
          this.snackbar = true
          this.timeout = 4000
        } finally {
          this.isediting = false
        }
      },
      subscribeGlobalUserSearchGates() {
        if (this.globalUserSearchGateUnsubs) {
          this.globalUserSearchGateUnsubs.forEach((unsub) => unsub())
        }
        const configUnsub = db
          .collection('config')
          .doc('globalUserSearch')
          .onSnapshot(
            (snap) => {
              const d = snap.exists ? snap.data() || {} : {}
              this.globalSearchHelpDeskAllowed = d.helpDeskAllowed !== false
            },
            () => {
              this.globalSearchHelpDeskAllowed = true
            }
          )
        const featureUnsub = db
          .collection('newfeatures')
          .doc('global-user-search')
          .onSnapshot(
            (snap) => {
              const f = snap.exists ? snap.data() || {} : {}
              this.globalUserSearchFeatureLive = f.active === true
            },
            () => {
              this.globalUserSearchFeatureLive = false
            }
          )
        this.globalUserSearchGateUnsubs = [configUnsub, featureUnsub]
      },
      userFilters(which) {
        if (which == 'show-leaders') {
          this.areLeadersVisible = !this.areLeadersVisible
        }
        if (which == 'show-archived') {
          this.areArchivedUsersVisible = !this.areArchivedUsersVisible
        }
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const normalizedPermissions = permissions.includes('superAdmin')
            ? [...permissions, 'helpDesk']
            : permissions

          const hasRole = normalizedPermissions.filter(() =>
            userRoles.some((role) => normalizedPermissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      isHelpDesk() {
        const userRoles = this.$store.getters['UserData/getRoles']
        return Array.isArray(userRoles) && userRoles.includes('helpDesk')
      },
      async archiveUser(user) {
        if (!user) {
          return Swal.fire({
            title: this.$t('users.messages.errorTitle'),
            text: this.$t('users.messages.errorArchive'),
            confirmButtonClass:
              'v-btn v-btn--contained theme--dark v-size--default v-btn--depressed',
          })
        }

        const actionKey = user.archived ? 'unarchive' : 'archive'
        const expectedPlaceholder = this.$t(
          `users.dialogs.${actionKey}.inputPlaceholder`
        )
        const expectedWord = actionKey.toUpperCase()

        const confirmed = await confirmAction({
          title: `${this.$t(`users.dialogs.${actionKey}.title`)} <br>"${
            user.email
          }"`,
          html: this.$t(`users.dialogs.${actionKey}.html`),
          input: 'text',
          inputPlaceholder: this.$t(
            `users.dialogs.${actionKey}.inputPlaceholder`
          ),
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (
                matchesConfirmWord(value, expectedWord, expectedPlaceholder)
              ) {
                resolve()
              } else {
                resolve(this.$t(`users.dialogs.${actionKey}.inputValidator`))
              }
            })
          },
          confirmButtonText: this.$t(
            `users.dialogs.${actionKey}.confirmButton`
          ),
          cancelButtonText: this.$t(`users.dialogs.${actionKey}.cancelButton`),
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        try {
          const archive = !user.archived
          await db.collection('users').doc(user.id).update({
            archived: archive,
          })

          user.archived = archive
          this.areArchivedUsersVisible = ''
          this.init(this.churchid)
        } catch (err) {
          console.error('Failed to archive user:', err)
        }
      },
      async purgeArchivedCoursesForUser(user) {
        if (!this.checkAuthorization(['superAdmin']) || this.isHelpDesk())
          return
        if (!user) return

        const uid = user.uid || user.id
        const cid = user.cid || this.churchid
        const email = user.email || user.user_name || 'this user'
        const roles = user.roles || []

        if (!uid || !cid) {
          await Swal.fire({
            title: 'Missing user identifiers',
            text: 'Unable to find user id or church id for this purge.',
          })
          return
        }
        if (roles.includes('superAdmin')) {
          await Swal.fire({
            title: 'Not allowed',
            text: 'Archived courses for superAdmins cannot be purged.',
          })
          return
        }

        const archivedSnap = await db
          .collection('currentcourses')
          .where('user', '==', uid)
          .where('cid', '==', cid)
          .where('status', '==', 'archived')
          .get()

        if (archivedSnap.empty) {
          await Swal.fire({
            title: 'No archived courses',
            text: `No archived courses found for ${email}.`,
          })
          return
        }

        const expectedInput = 'PURGE'
        const confirmed = await this.$swal.fire({
          title: 'Purge archived courses?',
          html: `This will remove ${archivedSnap.size} archived course(s) for <br>"${email}".<br><br>Type <strong>${expectedInput}</strong> to confirm.`,
          input: 'text',
          inputPlaceholder: `Type ${expectedInput}`,
          inputValidator: (value) => {
            if (value === expectedInput) return null
            return 'Please type PURGE to continue.'
          },
          showCancelButton: true,
          confirmButtonText: 'Purge',
          cancelButtonText: 'Cancel',
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed.isConfirmed) return

        this.$swal.fire({
          title: 'Purging archived courses...',
          text: `Removing ${archivedSnap.size} archived course(s)...`,
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
          didOpen: () => {
            this.$swal.showLoading()
          },
        })

        try {
          const result = await purgeArchivedUserCourses({ uid, cid })
          const summary = `Removed ${result.deletedCurrentCourses} archived course(s).`
          const details = `Deleted ${result.deletedCourseContent} coursecontent doc(s), ${result.deletedStorage} course JSON(s), ${result.deletedMissions} mission JSON(s).`
          const warning = result.errors.length
            ? `<br>${result.errors.length} item(s) could not be removed.`
            : ''

          if (result.errors.length) {
            this.$store.dispatch('AppData/logError', {
              message: 'Archived course purge completed with errors',
              stack: JSON.stringify(result.errors).slice(0, 2000),
              timestamp: firebase.firestore.FieldValue.serverTimestamp(),
              component: 'Users.vue',
              function: 'purgeArchivedCoursesForUser',
              additionalInfo: {
                uid,
                cid,
                errors: result.errors,
              },
            })
          }

          await this.$swal.fire({
            title: result.errors.length
              ? 'Completed with warnings'
              : 'Complete',
            html: `${summary}<br>${details}${warning}`,
          })
        } catch (error) {
          this.$store.dispatch('AppData/logError', {
            message: 'Archived course purge failed',
            stack: error.stack,
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            component: 'Users.vue',
            function: 'purgeArchivedCoursesForUser',
            additionalInfo: { uid, cid },
          })
          await this.$swal.fire({
            title: 'Purge failed',
            text: error?.message || 'Unable to purge archived courses.',
          })
        }
      },
      selectPurgeUser(user) {
        this.selectedPurgeUser = user
        this.purgeSearch = user?.label || ''
      },
      confirmPurgeSelection() {
        this.purgeDialog = false
        if (this.selectedPurgeUser) {
          this.purgeArchivedCoursesForUser(this.selectedPurgeUser)
        }
      },
      async deactivateAllUsers() {
        const cid = this.churchid

        const expectedPlaceholder = this.$t(

          'users.dialogs.deactivateAll.inputPlaceholder'

        )

        const expectedWord = 'DEACTIVATE'

        const confirmed = await confirmAction({
          title: this.$t('users.dialogs.deactivateAll.title'),
          html: this.$t('users.dialogs.deactivateAll.html'),
          input: 'text',
          inputPlaceholder: this.$t(
            'users.dialogs.deactivateAll.inputPlaceholder'
          ),
          inputValidator: (value) => {
            return new Promise((resolve) => {
              if (
                matchesConfirmWord(value, expectedWord, expectedPlaceholder)
              ) {
                resolve()
              } else {
                resolve(this.$t('users.dialogs.deactivateAll.inputValidator'))
              }
            })
          },
          confirmButtonText: this.$t(
            'users.dialogs.deactivateAll.confirmButton'
          ),
          cancelButtonText: this.$t('users.dialogs.deactivateAll.cancelButton'),
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        try {
          const snapshot = await db
            .collection('users')
            .where('cid', '==', cid)
            .get()

          if (snapshot.empty) {
            console.log('No users to deactivate.')
            return
          }

          let batchArray = [db.batch()]
          let operationCounter = 0
          let batchIndex = 0

          snapshot.forEach((doc) => {
            batchArray[batchIndex].update(doc.ref, { active: null })
            operationCounter++

            if (operationCounter === 499) {
              batchArray.push(db.batch())
              batchIndex++
              operationCounter = 0
            }
          })

          for (const batch of batchArray) {
            await batch.commit()
          }

          console.log('All users deactivated.')
          this.init(cid)
        } catch (error) {
          console.error('Error deactivating users:', error)
        }
      },
      filterByMonth(month) {
        this.filtered = month
      },
      lookUpGroup(uid) {
        if (!uid) return ''
        return this.groupNameByUidMap.get(uid) || ''
      },
      checkForLeader(item) {
        // console.log('isLeader ',item)
        if (item.roles.includes('leader')) return true
      },
      row_classes(item) {
        if (!item || !item.roles || !Array.isArray(item.roles)) return ''

        if (item.archived) return 'disabled'
        if (
          item.roles.includes('superAdmin') ||
          item.roles.includes('helpDesk')
        )
          return 'super-admin'

        return ''
      },
      async destroyUser() {
        if (this.isHelpDesk && this.isHelpDesk()) {
          const key = 'users.messages.deleteNotAllowed'
          this.feedback =
            (this.$te && this.$te(key) ? this.$t(key) : null) ||
            'Help Desk users cannot delete records.'
          return
        }

        const cid = this.churchid
        // Church admins may only read users in their own church (firestore.rules)
        const scopeToChurch = (ref) =>
          this.checkAuthorization(['superAdmin']) ? ref : ref.where('cid', '==', cid)
        this.$swal
          .fire({
            title: this.$t('users.dialogs.removeUser.title'),
            html: this.$t('users.dialogs.removeUser.html'),
            input: 'email',
            inputAttributes: {
              autocapitalize: 'off',
            },
            inputPlaceholder: this.$t(
              'users.dialogs.removeUser.inputPlaceholder'
            ),
            buttonsStyling: false,
            customClass: {
              container: this.$isDarkTheme() ? 'dark' : 'light',
              popup: this.$isDarkTheme() ? 'dark' : 'light',
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
            showCancelButton: true,
            confirmButtonText: 'Look up',
            showLoaderOnConfirm: true,
            preConfirm: (dataFromPreviousStep) => {
              if (dataFromPreviousStep !== '') {
                // look up user
                return scopeToChurch(
                  db
                    .collection('users')
                    .where('email', '==', dataFromPreviousStep)
                )
                  .get()
                  .then((snapshot) => {
                    if (snapshot.empty) {
                      throw new Error('This user does not exists')
                    } else {
                      // make sure the user is NOT a SuperAdmin
                      const roles = snapshot.docs[0].data().roles || []
                      if (
                        roles.includes('superAdmin') ||
                        roles.includes('helpDesk')
                      ) {
                        throw new Error('You can not remove a Super Admin')
                      }
                      return snapshot.docs[0].data()
                    }
                  })
                  .catch((error) => {
                    this.$swal.showValidationMessage(`Request failed: ${error}`)
                  })
              } else {
                throw new Error('You need to type an email')
              }
            },
          })
          .then((result) => {
            if (result.isConfirmed) {
              this.$swal
                .fire({
                  title: `${this.$t(
                    'users.dialogs.destroy.title'
                  )} <br><em><span style="color:red;">${
                    result.value.email
                  }</span></em><br>This cannot be undone. Are you sure?`,
                  text: this.$t('users.dialogs.destroy.text'),
                  animation: false,
                  showCancelButton: true,
                  buttonsStyling: false,
                  input: 'text',
                  inputPlaceholder: this.$t(
                    'users.dialogs.destroy.inputPlaceholder'
                  ),
                  customClass: {
                    container: this.$isDarkTheme() ? 'dark' : 'light',
                    popup: this.$isDarkTheme() ? 'dark' : 'light',
                    confirmButton:
                      'destroy swal-confirm-btn text-white text-uppercase text-body-2',
                    cancelButton:
                      'swal-cancel-btn text-white text-uppercase text-body-2',
                  },
                  reverseButtons: true,
                  inputValidator: (value) => {
                    return new Promise((resolve) => {
                      if (
                        matchesConfirmWord(
                          value,
                          'DESTROY',
                          this.$t('users.dialogs.destroy.inputPlaceholder')
                        )
                      ) {
                        resolve()
                      } else {
                        resolve(this.$t('users.dialogs.destroy.inputValidator'))
                      }
                    })
                  },
                  confirmButtonText: this.$t(
                    'users.dialogs.destroy.confirmButton'
                  ),
                  cancelButtonText: this.$t(
                    'users.dialogs.destroy.cancelButton'
                  ),
                })
                .then(async (res) => {
                  if (
                    res.value &&
                    matchesConfirmWord(
                      res.value,
                      'DESTROY',
                      this.$t('users.dialogs.destroy.inputPlaceholder')
                    )
                  ) {
                    // show a processing message in the popup
                    this.$swal.fire({
                      title: this.$t('users.dialogs.processing.title'),
                      text: this.$t('users.dialogs.processing.text'),
                      allowOutsideClick: false,
                      allowEscapeKey: false,
                      allowEnterKey: false,
                      onOpen: () => {
                        this.$swal.showLoading()
                      },
                    })

                    let storage = firebase.storage()
                    let storageRef = storage.ref()
                    var batch = db.batch()

                    // delete from the current courses collection
                    let currentCourseRef = scopeToChurch(
                      db
                        .collection('currentcourses')
                        .where('user', '==', result.value.uid)
                    )
                    let allCurrentCourses = await currentCourseRef.get()
                    for (const doc of allCurrentCourses.docs) {
                      batch.delete(doc.ref)

                      const theDoc = doc.data()

                      // delete from the course content collection, which is
                      // keyed by the enrollment (currentcourses doc) id
                      let courseContentRef = db
                        .collection('coursecontent')
                        .where('courseID', '==', doc.id)
                      let allCourseContent = await courseContentRef.get()
                      for (const doc of allCourseContent.docs) {
                        batch.delete(doc.ref)
                      }

                      // delete the course from the storage bucket
                      if (theDoc.storageRef) {
                        let courseStorageRef = storageRef.child(
                          theDoc.storageRef
                        )
                        courseStorageRef
                          .delete()
                          .then(() => {
                            console.log(
                              'courseStorageRef for ' +
                                result.value.uid +
                                ' have been removed'
                            )
                          })
                          .catch((error) => {
                            console.error(
                              `file delete coursestorage error occured: ${error}`
                            )
                          })
                      }
                    }

                    // notificationsread docs are private to their user
                    // (firestore.rules); deleting the users doc removes them
                    // (deleteUserFromAuthentication).

                    // delete from the userstate collection
                    let userstateRef = scopeToChurch(
                      db
                        .collection('userstate')
                        .where('email', '==', result.value.email)
                    )
                    let theUserstate = await userstateRef.get()
                    for (const doc of theUserstate.docs) {
                      batch.delete(doc.ref)
                    }

                    // delete from the pendingusers collection
                    let pendingUserRef = scopeToChurch(
                      db
                        .collection('pendingusers')
                        .where('email', '==', result.value.email)
                    )
                    let thePendingUser = await pendingUserRef.get()
                    for (const doc of thePendingUser.docs) {
                      batch.delete(doc.ref)
                    }

                    // delete member from the group collection
                    let groupMemberRef = scopeToChurch(
                      db
                        .collection('groups')
                        .where('members', 'array-contains', result.value.uid)
                    )
                    let member = await groupMemberRef.get()
                    for (const doc of member.docs) {
                      batch.update(doc.ref, {
                        members: firebase.firestore.FieldValue.arrayRemove(
                          result.value.uid
                        ),
                      })
                    }

                    // delete group (if leader) from the group collection
                    let theGroupLeaderRef = scopeToChurch(
                      db
                        .collection('groups')
                        .where('leader', '==', result.value.uid)
                    )
                    let theGroupLeader = await theGroupLeaderRef.get()
                    for (const doc of theGroupLeader.docs) {
                      batch.delete(doc.ref)
                    }

                    // delete the avatar from storage
                    if (
                      result.value.fullPath !== null &&
                      result.value.fullPath !== undefined
                    ) {
                      let avatarRef = storageRef.child(result.value.fullPath)
                      if (avatarRef) {
                        avatarRef
                          .delete()
                          .then(() => {
                            console.log(
                              'avatar for ' +
                                result.value.email +
                                ' have been removed'
                            )
                          })
                          .catch((error) => {
                            console.error(
                              `file delete avatar error occured: ${error}`
                            )
                          })
                      }
                    }

                    // delete from the users collection
                    let theUserRef = scopeToChurch(
                      db
                        .collection('users')
                        .where('email', '==', result.value.email)
                    )
                    let theUser = await theUserRef.get()
                    for (const doc of theUser.docs) {
                      batch.delete(doc.ref)
                    }

                    batch
                      .commit()
                      .then(() => {
                        console.log(
                          'user ' + result.value.email + ' has been removed'
                        )
                        this.snackbar = true
                        this.timeout = -1
                        this.snackbarMsg =
                          'User <strong><i>' +
                          result.value.email +
                          '</i></strong> has been ultimately destroyed'
                        this.$swal.close()
                        this.init(cid)
                      })
                      .catch((error) => {
                        console.error(
                          `file delete group error occured: ${error}`
                        )
                      })
                  }
                })
            }
          })
      },
      expires(user) {
        if (
          user.activated &&
          user.activated !== undefined &&
          user.activated !== null &&
          user.activated !== ''
        ) {
          let start = new Date(user.activated)
          // start.setDate(start.getDate() + 365)
          start.setFullYear(start.getFullYear() + 1)
          start = formatDate(start)
          return start
        } else {
          return 'never'
        }
      },
      setShowHeaders() {
        localStorage.setItem(
          'setUsersHeaders',
          JSON.stringify(this.selectedHeaders)
        )
      },
      async addTestUser() {
        const testUser = {
          activated: Date.now() + 60 * 60 * 1000,
          active: false,
          archived: false,
          cid: this.churchid || 'PLOL',
          email: 'dummyuser@test.com',
          emailVerified: true,
          fullPath: null,
          joined: Date.now(),
          possibleLeader: true,
          profileComplete: true,
          roles: ['member', 'leader', 'dummy'],
          uid: 'dummy_g80K2bwUUPhoEsI94zaqgCk7qPr2',
          url: null,
          user_name: 'Dummy User',
        }

        try {
          await db.collection('users').doc(testUser.uid).set(testUser)
          this.snackbar = true
          this.snackbarMsg = `✅ ${this.$t('users.messages.testUserAdded', {
            name: testUser.user_name,
          })}`
          this.init(this.churchid)
        } catch (err) {
          console.error('Failed to add test user:', err)
          this.snackbar = true
          this.snackbarMsg = `❌ ${this.$t('users.messages.testUserFailed')}`
        }
      },
      finalizeUserList() {
        if (
          this.isAuthenticated &&
          !this.checkAuthorization(['superAdmin'])
        ) {
          this.users = this.users.filter(
            (item) =>
              !item.roles.includes('superAdmin') &&
              !item.roles.includes('helpDesk')
          )
        }

        this.users = this.users.filter(
          (user, index, self) =>
            index === self.findIndex((u) => u.email === user.email)
        )

        this.users.sort((a, b) => {
          const aRoles = Array.isArray(a.roles) ? a.roles : []
          const bRoles = Array.isArray(b.roles) ? b.roles : []
          const aIsSuper =
            aRoles.includes('superAdmin') || aRoles.includes('helpDesk')
          const bIsSuper =
            bRoles.includes('superAdmin') || bRoles.includes('helpDesk')

          if (aIsSuper && bIsSuper) {
            return a.joined - b.joined
          }

          if (aIsSuper) return -1
          if (bIsSuper) return 1

          return 0
        })
      },
      onGlobalSearchInput() {
        if (this.globalSearchDebounceHandle) {
          clearTimeout(this.globalSearchDebounceHandle)
        }
        const trimmed = (this.globalUserSearch || '').trim()
        if (trimmed.length < 2) {
          this.globalSearchDebounceHandle = setTimeout(() => {
            this.globalSearchDebounceHandle = null
            if (this.churchid) {
              this.init(this.churchid)
            }
          }, 350)
          return
        }
        this.globalSearchDebounceHandle = setTimeout(() => {
          this.globalSearchDebounceHandle = null
          this.executeGlobalUserSearch(trimmed)
        }, 400)
      },
      async executeGlobalUserSearch(q) {
        const reqId = ++this.globalSearchRequestSeq
        this.globalSearchLoading = true
        try {
          const fn = firebase
            .app()
            .functions('us-central1')
            .httpsCallable('searchUsersGlobal')
          const res = await fn({ q, limit: 80 })
          if (reqId !== this.globalSearchRequestSeq) return

          this.users = res.data.users || []
          this.groups = []
          this.finalizeUserList()
          await this.loadGroupsForCids(this.users)

          if (res.data.hitScanCap) {
            this.snackbarMsg = this.$t('users.globalSearchScanCap')
            this.snackbar = true
          }
        } catch (err) {
          if (reqId !== this.globalSearchRequestSeq) return
          console.error('Global user search failed:', err?.code, err?.message, err)
          const code = err && err.code
          let msgKey = 'users.globalSearchError'
          if (code === 'functions/not-found') {
            msgKey = 'users.globalSearchErrorNotDeployed'
          } else if (
            code === 'permission-denied' ||
            code === 'functions/permission-denied'
          ) {
            const msg = String(err.message || '')
            if (msg.includes('Help desk access')) {
              msgKey = 'users.globalSearchErrorHelpDeskOff'
            } else if (msg.includes('not active')) {
              msgKey = 'users.globalSearchErrorFeatureOff'
            } else {
              msgKey = 'users.globalSearchErrorPermission'
            }
          } else if (
            code === 'unauthenticated' ||
            code === 'functions/unauthenticated'
          ) {
            msgKey = 'users.globalSearchErrorAuth'
          }
          this.snackbarMsg = this.$t(msgKey)
          this.snackbar = true
        } finally {
          if (reqId === this.globalSearchRequestSeq) {
            this.globalSearchLoading = false
          }
        }
      },
      async loadGroupsForCids(users) {
        const cids = [
          ...new Set(
            (users || []).map((u) => u.cid).filter((c) => c != null && c !== '')
          ),
        ]
        this.groups = []
        const chunkSize = 10
        for (let i = 0; i < cids.length; i += chunkSize) {
          const chunk = cids.slice(i, i + chunkSize)
          const snapshot = await db
            .collection('groups')
            .where('cid', 'in', chunk)
            .get()
          snapshot.forEach((doc) => {
            const group = doc.data()
            group.group_doc_id = doc.id
            this.groups.push(group)
          })
        }
      },
      init(cid) {
        if (!cid) {
          console.error('Missing cid in init()')
          return
        }

        this.isloading = true

        setTimeout(() => {
          this.users = []
          this.groups = []

          const usersRef = db.collection('users').where('cid', '==', cid)
          const groupsRef = db.collection('groups').where('cid', '==', cid)

          usersRef
            .get()
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                const user = doc.data()
                user.id = doc.id
                this.users.push(user)
              })
              return groupsRef.get()
            })
            .then((snapshot) => {
              snapshot.forEach((doc) => {
                const group = doc.data()
                group.group_doc_id = doc.id
                this.groups.push(group)
              })
              this.finalizeUserList()
              this.isloading = false
            })
            .catch((err) => {
              console.error('[INIT] Users/groups load failed:', err)
              this.isloading = false
            })
        }, 1000)
      },
    },
    created() {
      try {
        const stored = localStorage.getItem('setUsersHeaders')
        const parsed = JSON.parse(stored)

        // Ensure only valid keys for this role (excludes phoneNumber for church admins)
        const validKeys = this.adminActions.map((a) => a.key)
        this.selectedHeaders = parsed.filter((key) => validKeys.includes(key))
        if (
          validKeys.includes('phoneNumber') &&
          !this.selectedHeaders.includes('phoneNumber')
        ) {
          const emailIdx = this.selectedHeaders.indexOf('email')
          if (emailIdx >= 0) {
            this.selectedHeaders.splice(emailIdx + 1, 0, 'phoneNumber')
          } else {
            this.selectedHeaders.push('phoneNumber')
          }
        }
      } catch (e) {
        // fallback to default keys
        this.selectedHeaders = this.adminActions
          .filter((s) => s.default)
          .map((s) => s.key)
      }

      this.subscribeGlobalUserSearchGates()

      // churchid watcher (immediate) loads the table when context is available
    },
    beforeUnmount() {
      if (this.globalUserSearchGateUnsubs) {
        this.globalUserSearchGateUnsubs.forEach((unsub) => unsub())
        this.globalUserSearchGateUnsubs = null
      }
    },
  }
</script>

<style>
  .users-container {
    margin-bottom: 25%;
  }
  .users-container .section-heading h1 {
    display: flex;
    align-items: center;
  }
  .users-container .users-icon {
    margin-right: 10px;
  }
  .users-container .users-action-bar {
    font-size: 15px;
  }
  #users .v-theme--light.v-btn.v-btn--disabled {
    color: rgba(0, 0, 0, 1) !important;
  }
  .v-select .v-select__slot .v-select__selections input[readonly='readonly'] {
    display: none;
  }
  .users-container .v-input {
    font-size: 0.875rem;
  }
  .users-container .user-name-edit-field .v-input.v-input--is-focused {
    min-width: 150px;
  }
  .users-container .activate-switch .v-input__control {
    height: 24px;
  }
  .v-theme--light tr.super-admin {
    background: #f3f3f3;
  }
  .v-theme--dark tr.super-admin {
    background: rgb(243, 243, 243, 0.17);
  }
  .activate-switch .v-theme--dark.v-icon {
    color: rgb(243, 243, 243, 0.2);
  }
  body
    .v-theme--light.v-data-table
    > .v-data-table__wrapper
    > table
    > tbody
    > tr.super-admin:hover:not(.v-data-table__expanded__content):not(
      .v-data-table__empty-wrapper
    ) {
    background: #ececec;
  }
  .users-container .users-email {
    display: flex;
    align-items: end;
  }
  .users-container .users-email.active {
    flex-direction: column;
    align-items: initial;
  }
  .users-container .users-email.active .possible-leader-select .v-input {
    margin: 0;
    padding: 0;
  }
  #users .archive-icon {
    transform: rotate(-90deg);
  }
  #users .archive-icon.archived {
    transform: rotate(90deg);
  }
  #users .archive-icon:focus {
    outline: none;
  }
  .v-input--focused {
    min-width: 200px;
    max-width: 300px;
    transition: all 0.2s ease-in-out;
  }
  .chip-light {
    color: #383935 !important;
    caret-color: #383935 !important;
  }
  .chip-dark {
    color: #a9a8a8 !important;
    caret-color: #2d2d2d !important;
  }

  /* Full-width row above totals; wider than toolbar search */
  .global-users-search-row .global-users-search {
    min-width: min(100%, 320px) !important;
    max-width: min(100%, 640px) !important;
    width: 100% !important;
  }

  .global-users-search-row .global-users-search .v-field,
  .global-users-search-row .global-users-search .v-field--focused,
  .global-users-search-row .global-users-search .v-input__control {
    min-width: min(100%, 320px) !important;
    max-width: min(100%, 640px) !important;
    width: 100% !important;
  }

  /* Fix size changes on focus for filter and search fields */
  .fixed-size-select,
  .fixed-size-search {
    min-width: 300px !important;
    max-width: 300px !important;
    width: 300px !important;
  }

  .fixed-size-select .v-field,
  .fixed-size-search .v-field {
    min-width: 300px !important;
    max-width: 300px !important;
    width: 300px !important;
  }

  .fixed-size-select .v-field--focused,
  .fixed-size-search .v-field--focused {
    min-width: 300px !important;
    max-width: 300px !important;
    width: 300px !important;
  }

  .fixed-size-select .v-input__control,
  .fixed-size-search .v-input__control {
    min-width: 300px !important;
    max-width: 300px !important;
    width: 300px !important;
  }
</style>
